// ===== WebSocket 핸들러 공용 헬퍼 =====
// DOM 앵커·귀속 판정·히스토리 렌더 등 ws-handlers가 공유하는 순수 헬퍼.
// classic script로 로드되며 ws-handlers.js보다 먼저 실려야 한다(index.html 순서).

type HumanMessageAttribution =
  | { kind: "self" }
  | { kind: "peer"; senderName: string; senderAvatar: string | null };
type WsStreamState = { bubble: HTMLElement; text: string; thinking: string; thinkingEl: HTMLDetailsElement | null };

function wsAgentProfile(agentId: string): { name: string; role: string; avatarDir: string } | undefined {
  return Object.entries(AGENT_PROFILES).find(([id]) => id === agentId)?.[1];
}
function requireWsElement(element: Element | null, label: string): HTMLElement {
  if (!(element instanceof HTMLElement)) throw new Error(`Missing ${label}`);
  return element;
}
const wsChatMessagesElement: HTMLElement = requireWsElement(document.getElementById("chatMessages"), "#chatMessages");
interface Window { _inboxReloadTimer?: number; lastSender: unknown }

// D07: 룸 소속 세션은 '이전 대화'(레거시) 섹션에서 제외 — 룸 목록으로만 노출.
// D14(Phase 3, SEC-R3): RoomList 미충전 시 룸 필터는 fail-open이라 공유 룸 세션(타 소유 가능)이
// 혼입될 수 있다 — ownerActorId가 있고 현재 뷰어와 다르면 RoomList 로딩 여부와 무관하게 제외한다
// (room-list.ts _latestNonRoomSessionId의 기존 ownerActorId 이중 대조 미러). ownerActorId가 없는
// 레거시 세션은 걸러내지 않는다(accepted risk).
function _excludeRoomSessions(sessions: CoreSessionSummary[]): CoreSessionSummary[] {
  const roomSessionIds = typeof RoomList === "undefined" ? null : RoomList.getSessionIds();
  const viewer = viewerActorId();
  return (sessions || []).filter((s) => {
    if (roomSessionIds && roomSessionIds.size > 0 && roomSessionIds.has(s.id)) return false;
    const ownerActorId = "ownerActorId" in s && typeof s.ownerActorId === "string" ? s.ownerActorId : null;
    if (viewer && ownerActorId && ownerActorId !== viewer) return false;
    return true;
  });
}

function ensureThinkingBlock(s: WsStreamState): void {
  if (s.thinkingEl) return;
  const el = document.createElement("details");
  el.className = "thinking-block";
  el.open = true;
  el.innerHTML = '<summary>추론 과정</summary><div class="thinking-content"></div>';
  // 스트리밍 버블은 .bubble-line(flex row: 버블 + × 취소 버튼) 안에 있으므로, thinking 블록은
  // 라인 컨테이너 앞(=버블 위)에 끼워 넣는다. 라인이 없는 레거시 구조는 버블 자신이 앵커.
  const anchor = s.bubble.closest<HTMLElement>(".bubble-line") || s.bubble;
  anchor.parentElement?.insertBefore(el, anchor);
  s.thinkingEl = el;
}

function formatRoomSenderLabel(actorId: unknown): string {
  if (typeof actorId !== "string" || !actorId.trim()) return "상대";
  const normalized = actorId.trim();
  const readable = normalized.includes(":")
    ? normalized.split(":").slice(1).join(":") || normalized
    : normalized;

  // 불투명 해시 id(예: user:53ffc90ba4c1f6b0)는 사람이 읽을 수 없다 — 원시 id를 노출하지 말고
  // 중립 라벨로 강등한다. 읽을 수 있는 슬러그(user:john-doe 등)만 기존 title-case 경로를 탄다.
  if (/^[0-9a-f]{12,}$/i.test(readable)) return "상대";

  return readable
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((part) => (/^[a-z]/i.test(part) ? `${part[0].toUpperCase()}${part.slice(1)}` : part))
    .join(" ") || "상대";
}

// 뷰어(현재 로그인 actor)의 actorId — 히스토리 self/peer 귀속 판정 기준.
// actorContext 미로드(부팅 직후)·비브라우저 샌드박스에서는 null — 호출자가 null을 "판정 불가"로 다룬다.
function viewerActorId(): string | null {
  if (typeof window === "undefined") return null;
  return window.BigtionAuth?.actorContext?.actor?.actor || null;
}

function resolveHumanMessageAttribution(message: {
  sender?: unknown;
  senderName?: unknown;
  senderAvatar?: unknown;
}): HumanMessageAttribution {
  const sender = typeof message.sender === "string" ? message.sender.trim() : "";
  const viewer = viewerActorId();
  if (sender && viewer && sender === viewer) return { kind: "self" };

  const providedName = typeof message.senderName === "string" ? message.senderName.trim() : "";
  const senderName = providedName && providedName !== sender
    ? providedName
    : formatRoomSenderLabel(sender);
  const providedAvatar = typeof message.senderAvatar === "string" ? message.senderAvatar.trim() : "";
  const senderAvatar = providedAvatar || null;
  return { kind: "peer", senderName, senderAvatar };
}

// 룸 실행 이벤트 자기/원격 판정 (Phase 3, T3 batch2 → 판정 순서 교정): 내 탭(clientId 일치)은
// 항상 자기 실행 — 로컬에서 항상 확정 가능한 신호를 최우선으로 본다. actor 비교(D04: 동일 계정
// 다른 탭 허용)는 viewerActorId가 채워진 뒤에만 유효하다. 이전 코드는 invokerActorId를 무조건
// 먼저 비교해서 actorContext가 아직 null인 순간 자기 실행을 원격으로 오분류했다(취소 버튼
// 미표시 결함). 둘 다 판정 불가면(비룸·레거시 이벤트) 자기 실행으로 간주해 기존 동작 유지.
function isRemoteRoomEvent(data: WsRemoteEvent): boolean {
  if (data.invokerClientId && data.invokerClientId === wsClient?.clientId) return false;
  const viewer = viewerActorId();
  if (data.invokerActorId && viewer) return data.invokerActorId !== viewer;
  if (data.invokerClientId) return data.invokerClientId !== wsClient?.clientId;
  return false;
}

function consumeCanonicalNotification(data?: WsPayload): void {
  const bridge = (globalThis as typeof globalThis & {
    BigtionAXPWA?: { consumeCanonicalNotificationSignal?: () => Promise<void> };
  }).BigtionAXPWA;
  if (typeof bridge?.consumeCanonicalNotificationSignal === "function") {
    void bridge.consumeCanonicalNotificationSignal();
  }
}

// 다른 방(세션)을 보는 중에 도착한 fan-out 이벤트인지 판정 (Phase 3, T3 batch2 공용 가드).
// 발원 sessionId가 현재 보고 있는 세션과 정확히 일치하는 이벤트만 통과시킨다.
function isOtherSessionEvent(data: WsPayload): boolean {
  return data.sessionId !== currentSessionId;
}

// P2(bigtionax-chat-mention-reply-reaction): 앵커 sessionId 해석 — 서버가 sessionId를 싣는 경로
// (room-message/session-history)는 그 값을, 못 싣는 경로(agent-complete/complete)는 currentSessionId로
// 폴백한다(§4).
function resolveAnchorSessionId(data: WsPayload): string | undefined {
  return typeof data.sessionId === "string" ? data.sessionId : (currentSessionId || undefined);
}

// 스트리밍 확정 시점(speaker-end/agent-complete/complete)에 이미 DOM에 있는 버블의 .message 루트에
// (sessionId, seq) 앵커를 채운다. closest는 옵셔널 호출로 mock 방어 유지 — peer-message-render 등
// 형제 테스트의 element mock에는 closest가 없다(§0-2).
// Phase 3(bigtionax-memory-read-g-source-marker): memorySourceCount는 onSpeakerEnd 스트리밍
// 분기(그룹채팅 화자 답변)에서만 실려 온다 — onAgentComplete(direct-Hana 완료 경로)는 CAP21 범위
// 밖이라 4번째 인자를 넘기지 않고, 그 경우 count는 undefined라 attach가 조용히 no-op한다.
function anchorStreamedMessage(bubble: HTMLElement | null | undefined, sessionId: string | undefined, seq: number | undefined, memorySourceCount?: number): void {
  const root = bubble?.closest?.(".message");
  if (root instanceof HTMLElement) {
    applyMessageAnchor(root, { sessionId, seq });
    // 2026-10-06 결정(B): 실시간 스트림으로 그려진 에이전트 말풍선도 seq가 확정되는 이 시점에 읽음
    // 대상으로 표시한다(방 세션만 — chat.ts markAgentReplyReceiptEligible). 형제 테스트가 chat.ts 없이
    // 이 파일만 실행할 수 있어 typeof 가드(appendReactionSlot 선례).
    if (typeof markAgentReplyReceiptEligible === "function") markAgentReplyReceiptEligible(root, sessionId, seq);
    // registerRenderedNode가 dedupe로 기존 노드를 돌려줄 수 있다 — 마커는 실제로 남는 노드에 붙여야
    // 한다(§설계, "registerRenderedNode 가 기존 노드를 돌려주면 그 노드에 attach").
    const renderedNode = chatTimelineNavigation.registerRenderedNode(root, { sessionId, seq });
    if (typeof MessageReadReceipts !== "undefined") {
      MessageReadReceipts.onMessageRendered(renderedNode instanceof HTMLElement ? renderedNode : root);
    }
    // P10(bigtionax-chat-mention-reply-reaction, §설계G 보완): 스트리밍 버블은 생성 시점(seq 미확정)
    // 에는 appendReactionSlot이 no-op이었다 — seq가 확정되는 이 시점에 다시 불러 반응 슬롯을 채운다
    // (멱등: 이미 슬롯이 있으면 chat.ts appendReactionSlot이 스킵).
    if (typeof appendReactionSlot === "function") appendReactionSlot(root);
    if (typeof MemorySourceMarker !== "undefined") {
      const markerTarget = renderedNode instanceof HTMLElement ? renderedNode : root;
      MemorySourceMarker.attach(markerTarget, { sessionId, seq, count: memorySourceCount });
    }
  }
}

// Phase 4(bigtionax-chat-attachments): 와이어 text에는 전송 시점(app.ts:190) 임베딩된
// `[첨부파일]\n<경로...>` 꼬리 블록이 그대로 실려 온다 — attachments가 구조화 렌더(renderAttachments)를
// 갖는 지금은 그 블록을 표시용 텍스트에서 제거해야 중복 표시를 막는다. 로그/와이어 원문(text/content
// 자체)은 이 함수 밖에서 불변으로 유지된다 — 이 함수는 렌더 직전 표시용 문자열만 가공한다.
function stripAttachmentBlockForDisplay(text: string, attachments?: unknown): string {
  if (!Array.isArray(attachments) || attachments.length === 0) return text;
  const paths = attachments
    .map((f) => (f && typeof f === "object" ? (f as { path?: unknown }).path : undefined))
    .filter((p): p is string => typeof p === "string");
  if (paths.length === 0) return text;
  const block = `[첨부파일]\n${paths.join("\n")}`;
  if (text === block) return "";
  const suffix = `\n\n${block}`;
  if (text.endsWith(suffix)) return text.slice(0, -suffix.length);
  return text;
}

// 히스토리 메시지 1건 렌더 (session-history 초기 로드 + load-more 공용, Phase 8)
// Phase 2(bigtionax-chat-session-attribution-contract, §설계b): 앵커 sessionId는 전역 currentSessionId
// 폴백 없이 호출부가 자기 프레임의 payload sessionId를 필수 인자로 넘긴다 — 전역이 이전 방 id인 순간에
// 렌더되어도 새 방의 타임라인에 이전 방 id가 각인되지 않는다.
var isRestoringChatHistory = false;

function renderHistoryMessage(msg: WsHistoryMessage, sessionId: string): Element | null {
  const previous = isRestoringChatHistory;
  isRestoringChatHistory = true;
  try {
    return renderHistoryMessageContent(msg, sessionId);
  } finally {
    isRestoringChatHistory = previous;
  }
}

function renderHistoryMessageContent(msg: WsHistoryMessage, sessionId: string): Element | null {
  const timeStr = msg.time ? formatChatTimestamp(msg.time) : undefined;

  // 영속 artifact 카드 분기 (Phase 2, T4 artifact-substrate): role은 "artifact"로 저장되지만
  // 렌더 분기는 msg.artifact 유무로 판정한다 — role 매칭에 의존하면 향후 role 확장 시 깨진다.
  if (msg.artifact) {
    return renderArtifactCard(msg.artifact, { time: timeStr, transfer: msg.transfer, seq: msg.seq, sessionId });
  }

  // P2: history 복원(초기+load-more) 공용 경로 — 4분기 모두 같은 앵커를 싣는다(§4).
  // P7: replyTo도 같은 4분기에 동일하게 전달한다(§F) — 없으면 키 자체가 없다(옵션 스프레드로 무영향).
  // Phase 3(bigtionax-memory-read-g-source-marker): memorySourceCount도 같은 4분기에 실린다
  // (agent:* 와 assistant/secretary 모두) — user/peer 분기로도 흘러가지만 그쪽 history 항목은
  // memorySourceCount가 없으므로(서버가 에이전트 응답에만 기록) attach의 count 가드에서 자연히
  // no-op된다.
  const anchor = { seq: msg.seq, sessionId, memorySourceCount: msg.memorySourceCount, ...(msg.replyTo ? { replyTo: msg.replyTo } : {}) };

  let node;
  if (msg.role === "user") {
    const displayContent = stripAttachmentBlockForDisplay(msg.content, msg.attachments);
    const attribution = resolveHumanMessageAttribution(msg);
    if (attribution.kind === "self") {
      node = addMessage("user", displayContent, { time: timeStr, attachments: msg.attachments, ...anchor });
    } else {
      node = addPeerMessage(
        attribution.senderName,
        attribution.senderAvatar,
        displayContent,
        { time: timeStr, attachments: msg.attachments, senderActorId: typeof msg.sender === "string" ? msg.sender : null, ...anchor },
      );
    }
  } else if (msg.role?.startsWith("agent:")) {
    const agentId = msg.role.split(":")[1];
    node = addAgentMessage(agentId, msg.content, { time: timeStr, ...anchor });
  } else {
    node = addMessage("secretary", msg.content, { time: timeStr, markdown: true, ...anchor });
  }

  // thinking 복원: 세션 전환 후에도 thinking 토글 표시
  if (msg.thinking && node) {
    const bubble: unknown = node.querySelector(".bubble");
    if (bubble instanceof HTMLElement) {
      const fakeStream: WsStreamState = { bubble, text: "", thinking: msg.thinking, thinkingEl: null };
      ensureThinkingBlock(fakeStream);
      const thinkingContent = fakeStream.thinkingEl?.querySelector<HTMLElement>(".thinking-content");
      if (thinkingContent) thinkingContent.textContent = msg.thinking;
    }
  }

  return node;
}

// 렌더층 캡(CAP21, Phase 9): 세션 최초 렌더(resume)는 최근 MAX_RENDER_MESSAGES개만 그린다.
// 서버(ws-handler.js MESSAGE_LAZY_LOAD_LIMIT=50)가 이미 캡을 두지만, 렌더층에서도 독립적으로
// 방어한다 — 저장/로드층 캡과는 별개 층. 이전 메시지는 스크롤업(load-more-messages)으로 로드.
const MAX_RENDER_MESSAGES = 50;
function capRenderMessages(messages: WsHistoryMessage[], max = MAX_RENDER_MESSAGES): WsHistoryMessage[] {
  if (!messages || messages.length <= max) return messages || [];
  return messages.slice(-max);
}

// 스크롤 업 시 이전 메시지 배치를 맨 위에 prepend + 스크롤 위치 보정 (Phase 8, CAP18)
// Phase 2(bigtionax-chat-session-attribution-contract, §설계c): 늦게 도착한 배치가 현재 보고 있는
// 방과 다르면 그대로 폐기한다 — 스크롤업 요청 이후 방을 전환한 사이 응답이 온 경우, 이전 방의
// 상태(messageListState/DOM)를 건드리지 않는다.
function onMoreMessages(data: WsPayload): void {
  if (data.sessionId !== currentSessionId) return;

  messageListState.loading = false;
  messageListState.hasMore = !!data.hasMore;
  messageListState.offset = data.offset ?? messageListState.offset;

  if (!data.messages || data.messages.length === 0) return;
  ingestCanonicalMessages(data, "prepend");

  // P10(bigtionax-chat-mention-reply-reaction, §설계H): load-more — 하드코딩한 상수가 아니라 이번에
  // 실제로 prepend된 배치의 seq 구간만 범위 조회한다.
  if (typeof MessageReactions !== "undefined") {
    const batchSeqs = data.messages
      .map((msg) => msg.seq)
      .filter((seq): seq is number => Number.isInteger(seq));
    if (batchSeqs.length > 0) {
      MessageReactions.restoreRange(data.sessionId, Math.min(...batchSeqs), Math.max(...batchSeqs));
    }
  }
}
